/**
 * @file 云钱包领取质押奖励相关hook
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  ClaimOption,
  Process,
  RewardType,
  ClaimEncodeStage,
  ClaimType,
} from '@/types/claim';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { postClaimEncode, postSaveClaimOrder } from '@/services/claim';
import BigNumber from 'bignumber.js';
import { debounce } from 'lodash-es';
import { getTokenAddress } from '@/config/address';
import { postSyncOrderStatus, postBindOrderTx } from '@/services/order';
import { scTrack } from '@web3/base-tracking';
import { runAsyncSafe } from '@web3/base-utils';
import { Event } from '@/config/track-config';
import { useFm } from '@web3/base-hooks';
import { ApproveFailType } from '@/types/common';
import { useChain, useCurrentChainAddress } from '@/hooks/wallet';
import { useToast } from '@web3/base-hooks';
import { GasStore } from '@/types/stake';
import { providers } from 'ethers';
import { useWallet, useWalletId } from '@/hooks/wallet';
import { stxAuthenticate, stxSignTrancaction } from '@/utils/stacks';
import { ChainCodeTypes } from '@/types/chain';
import { PostClaimEncodeParams } from '@/types/claim';
import { useSuiSignTransaction } from '@/containers/operation/transactionHooks/suiTransactionHook';
import { VersionedTransaction } from '@solana/web3.js';
import bs58 from 'bs58';

export const useClaimStage = () => {
  const [claimStage, setClaimStage] = useState(ClaimEncodeStage.UnKnow);

  const onChangeClaimStage = (v: ClaimEncodeStage) => {
    setClaimStage(v);
  };

  return {
    claimStage,
    onChangeClaimStage,
  };
};

/**
 * gasfee相关hook
 */
export const useGasFee = (option: Partial<ClaimOption>) => {
  const { process, poolInfo, hasReward, rewardFunds, onChangeClaimStage } =
    option as ClaimOption;
  const [gasAmount, setGasAmount] = useState('');
  const [gasFeeIsLoading, setGasFeeIsLoading] = useState(false);
  const { coinInfoStore } = useCoinInfoContext();
  const [loopPercent, setLoopPercent] = useState(0);
  const uid = useRef(0);
  const tokenAddress = getTokenAddress(poolInfo.chainCode);
  const gasToken = coinInfoStore?.[`${poolInfo.chainCode}${tokenAddress}`];
  const gasFeePrice = BigNumber(gasAmount || 0)
    .times(gasToken?.price)
    .toString();
  const timer = useRef<NodeJS.Timeout>();
  const { walletAddress, pubKey } = useCurrentChainAddress(poolInfo.chainCode);
  const { toastWarn } = useToast();
  const gasStore = {
    hasGasFee: !!gasAmount,
    amount: gasAmount || '0',
    price: gasFeePrice || '0',
    symbol: gasToken?.symbol || '',
    isLoading: gasFeeIsLoading,
    loopPercent: loopPercent * 20,
  } as GasStore;

  const fetchGasFee = async (selfUid: number) => {
    setGasFeeIsLoading(true);
    const params: PostClaimEncodeParams = {
      chainId: poolInfo.chainId,
      poolId: poolInfo.id,
      slippage: '0.5',
      walletAddress,
      pubKey,
    };

    if (poolInfo.chainCode === ChainCodeTypes.SUI) {
      if (Array.isArray(rewardFunds) && rewardFunds.length) {
        params.requestInfos = rewardFunds;
      } else {
        // 在sui链中，领取奖励时如果获取不到rewardFunds，不请求encode接口
        return;
      }
    }

    const { success, result, message } = await postClaimEncode(params);

    if (selfUid !== uid.current) return;

    setGasFeeIsLoading(false);

    if (!success) {
      toastWarn(message);
      return false;
    }

    const data = result.txDataList[0];
    setGasAmount(data.gasFee);
    onChangeClaimStage(result.stage);

    return true;
  };

  const startLoopGasFee = useCallback(
    debounce(async (percent = 0) => {
      clearTimeout(timer.current);

      setLoopPercent(percent);

      if (percent === 0) {
        uid.current = Math.random();
        const result = await fetchGasFee(uid.current);
        // 报错则取消轮循
        if (!result) return;
      }

      timer.current = setTimeout(() => {
        if (percent === 4) {
          setLoopPercent(5);
          startLoopGasFee(0);
        } else {
          startLoopGasFee(percent + 1);
        }
      }, 2000);
    }, 500),
    [rewardFunds]
  );

  useEffect(() => {
    if (process === Process.None) {
      clearTimeout(timer.current);
    }

    return () => {
      clearTimeout(timer.current);
      uid.current = 0;
    };
  }, [process, startLoopGasFee]);

  useEffect(() => {
    if (hasReward) {
      startLoopGasFee();
    }
  }, [hasReward]);

  return {
    gasStore,
    startLoopGasFee,
  };
};

/**
 * 封装了一些代码行数比较多的并且可以复用的请求
 */
export const useFetchStore = (option: Partial<ClaimOption>) => {
  const { poolInfo } = option as ClaimOption;
  const { toastWarn } = useToast();
  const { walletAddress, pubKey } = useCurrentChainAddress(poolInfo.chainCode);
  const fm = useFm();
  const { id } = poolInfo;

  // 生成staking订单
  const createOrder = async (stage: ClaimEncodeStage) => {
    const { success, result } = await await postSaveClaimOrder({
      chainId: +poolInfo.chainId,
      poolId: id,
      slippage: '0.5',
      stage,
      walletAddress,
    });

    if (!success) {
      toastWarn(fm('mpsToastUpcoming'));
    }

    return {
      success,
      result,
    };
  };

  // 同步给订单错误态
  const updateFailedOrderStatus = async (
    isCancel: boolean,
    orderCode: string,
    orderTxId: number
  ) => {
    await postSyncOrderStatus({
      chainId: +poolInfo.chainId,
      orderCode,
      orderTxId,
      status: isCancel ? ApproveFailType.CANCEL : ApproveFailType.UN_KNOWN,
      walletAddress,
    });
  };

  // 同步给订单成功态
  const updateSuccessOrderStatus = async (
    hash: string,
    orderCode: string,
    orderTxId: number
  ) => {
    await postBindOrderTx({
      chainId: +poolInfo.chainId,
      orderCode,
      orderTxId,
      txHash: hash,
      walletAddress,
    });
  };

  // 获取编码
  const getEncodeData = async (rewardFunds?: string[]) => {
    let success = true;

    const params: PostClaimEncodeParams = {
      chainId: +poolInfo.chainId,
      poolId: id,
      slippage: '0.5',
      walletAddress,
      pubKey,
    };

    if (Array.isArray(rewardFunds) && rewardFunds.length) {
      params.requestInfos = rewardFunds;
    }
    const {
      success: encodeSuccess,
      result,
      message,
    } = await postClaimEncode(params);

    if (!encodeSuccess) {
      toastWarn(message);
      success = false;
    }

    if (!result.txDataList[0]) {
      toastWarn(fm('systemBusyTryLater'));
      success = false;
    }

    return {
      success,
      result,
    };
  };

  return {
    createOrder,
    updateFailedOrderStatus,
    updateSuccessOrderStatus,
    getEncodeData,
  };
};

/**
 * 开始领取奖励相关hook
 */
export const useClaim = (option: Partial<ClaimOption>) => {
  const {
    rewardValue,
    gasStore,
    process,
    poolInfo,
    claimType,
    rewardFunds,
    onChangeClaimStage,
    onChangeProcess,
  } = option as ClaimOption;
  const [confirmButtonIsLoading, setConfirmButtonIsLoading] = useState(false);
  const { switchToPoolChain } = useChain();
  const { toastWarn, toastSuccess } = useToast();
  const [orderCode, setOrderCode] = useState('');
  const gasFeeIsGreatThanProfit = BigNumber(rewardValue).lt(gasStore.price);
  const { evmThirdPartyProvider, solanaThirdPartyProvider, currentWalletType } =
    useWallet();
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);
  const fm = useFm();
  const {
    createOrder,
    updateFailedOrderStatus,
    updateSuccessOrderStatus,
    getEncodeData,
  } = useFetchStore(option);
  const walletId = useWalletId();
  const { suiSignAndTransaction } = useSuiSignTransaction();

  const evmClaim = async () => {
    // 切链
    const switchChainIsSuccess = await switchToPoolChain(poolInfo.chainId);
    if (!switchChainIsSuccess) return;

    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData();

    if (!encodeSuccess) return;

    // 创建订单
    const {
      success: saveOrderSuccess,
      result: { orderCode, orderTxId },
    } = await createOrder(result.stage);

    if (!saveOrderSuccess) return;

    let txHash = '';

    try {
      const txData = result.txDataList[0];
      const provider = new providers.Web3Provider(evmThirdPartyProvider);
      const signer = provider.getSigner();
      // 签名
      txHash = (
        await signer.sendTransaction({
          from: txData.from,
          to: txData.to,
          data: txData.data,
          value: txData.value,
        })
      ).hash;
    } catch (e: any) {
      // 是否为手动拒绝
      const isCancel = e?.code?.includes?.('ACTION_REJECTED');

      if (!isCancel) {
        toastWarn(fm('systemBusyTryLater'));
      }
      // 保存状态
      updateFailedOrderStatus(isCancel, orderCode, orderTxId);
      return;
    }

    // 同步订单成功态
    updateSuccessOrderStatus(txHash, orderCode, orderTxId);

    if (result.stage === ClaimEncodeStage.ApproveTrade) {
      onChangeProcess(Process.Result);
      setOrderCode(orderCode);
    }

    if (result.stage === ClaimEncodeStage.ApproveAmount) {
      toastSuccess(fm('success'));
      onChangeClaimStage(ClaimEncodeStage.ApproveTrade);
    }
  };

  const stacksClaim = async () => {
    // 授权
    const { success: authSuccess } = await stxAuthenticate(walletAddress);

    if (!authSuccess) return;

    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData();

    if (!encodeSuccess) return;

    // 创建订单
    const {
      success: saveOrderSuccess,
      result: { orderCode, orderTxId },
    } = await createOrder(result.stage);

    if (!saveOrderSuccess) return;

    const { success: signSuccess, hash: txHash } = await stxSignTrancaction(
      result.txDataList[0].data
    );

    if (!signSuccess) {
      updateFailedOrderStatus(true, orderCode, orderTxId);
      return;
    }

    // 同步订单成功态
    updateSuccessOrderStatus(txHash, orderCode, orderTxId);

    if (result.stage === ClaimEncodeStage.ApproveTrade) {
      onChangeProcess(Process.Result);
      setOrderCode(orderCode);
    }

    if (result.stage === ClaimEncodeStage.ApproveAmount) {
      toastSuccess(fm('success'));
      onChangeClaimStage(ClaimEncodeStage.ApproveTrade);
    }
  };

  const suiClaim = async () => {
    // 获取编码
    const { success: encodeSuccess, result } = await getEncodeData(rewardFunds);

    if (!encodeSuccess) {
      toastWarn(fm('systemBusyTryLater'));
      return;
    }
    // 创建订单
    const {
      success: saveOrderSuccess,
      result: { orderCode, orderTxId },
    } = await createOrder(result.stage);

    if (!saveOrderSuccess) return;

    // 钱包签名
    const [errTx, transactionResult] = await suiSignAndTransaction(
      result.txDataList[0].data
    );

    if (errTx) {
      const isReject = errTx.message.indexOf('reject') > -1;
      toastWarn(
        isReject ? fm('walletRejected') : fm('submitTransactionFailed')
      );
      // 同步订失败状态
      updateFailedOrderStatus(isReject, orderCode, orderTxId);
      return;
    }
    // 同步订单成功状态
    updateSuccessOrderStatus(transactionResult.digest, orderCode, orderTxId);

    // // 结束
    if (result.stage === ClaimEncodeStage.ApproveTrade) {
      onChangeProcess(Process.Result);
      setOrderCode(orderCode);
    }

    if (result.stage === ClaimEncodeStage.ApproveAmount) {
      toastSuccess(fm('success'));
      onChangeClaimStage(ClaimEncodeStage.ApproveTrade);
    }
  };

  const solClaim = async () => {
    const { success: encodeSuccess, result } = await getEncodeData();

    if (!encodeSuccess) return;

    const provider = solanaThirdPartyProvider;

    const transaction = VersionedTransaction.deserialize(
      bs58.decode(result.txDataList[0].data)
    );

    const [errTx, tx] = await runAsyncSafe<
      { signatures: Array<Uint8Array>; serialize: () => Uint8Array },
      Error
    >(provider.signTransaction(transaction));
    if (errTx) {
      const isReject = errTx.message.indexOf('reject') > -1;
      toastWarn(
        isReject ? fm('walletRejected') : fm('submitTransactionFailed')
      );
      return;
    }

    const txDataList = result.txDataList[0];
    const { success, result: OrderResult } = await postSaveClaimOrder({
      chainId: +poolInfo.chainId,
      poolId: poolInfo.id,
      slippage: '0.5',
      stage: result.stage,
      walletAddress,
      orderCode: result.orderCode,
      broadcast: 1,
      broadcastData: [
        {
          signData: bs58.encode(tx.serialize()),
          chainId: +poolInfo.chainId,
          from: txDataList?.from ?? '',
          to: txDataList?.to ?? '',
          value: txDataList?.value ?? '',
          txHash: bs58.encode(tx.signatures[0]),
        },
      ],
      walletId: walletId?.walletId,
      walletType: currentWalletType,
      useThirdConfig: false,
    });

    if (!success) {
      toastWarn(fm('systemBusyTryLater'));
      return;
    }

    if (success) {
      onChangeProcess(Process.Result);
      setOrderCode(OrderResult.orderCode);
    }
  };

  const beginClaim = async () => {
    setConfirmButtonIsLoading(true);
    scTrack(Event.Web3UserClick, {
      page_name:
        claimType === ClaimType.Claim
          ? 'StakingWeb_bbSOLrewards'
          : 'StakingWeb_bbSOLPagewithdrawal',
      button_name:
        claimType === ClaimType.Claim ? 'rewards_confirm' : 'principal_confirm',
    });

    // 如果是stx链则走stacks质押逻辑
    if (poolInfo.chainCode === ChainCodeTypes.STX) {
      await stacksClaim();
      setConfirmButtonIsLoading(false);
      return;
    }

    if (poolInfo.chainCode === ChainCodeTypes.SUI) {
      await suiClaim();
      setConfirmButtonIsLoading(false);
      return;
    }

    if (poolInfo.chainCode === ChainCodeTypes.SOL) {
      await solClaim();
      setConfirmButtonIsLoading(false);
      return;
    }

    await evmClaim();
    setConfirmButtonIsLoading(false);
  };

  useEffect(() => {
    if (process === Process.None) {
      setConfirmButtonIsLoading(false);
    }
  }, [process]);

  return {
    orderCode,
    confirmButtonIsLoading,
    gasFeeIsGreatThanProfit,
    beginClaim,
  };
};

/**
 * 领取奖励按钮相关hook
 */
export const useButtonStatus = (option: Partial<ClaimOption>) => {
  const fm = useFm();
  const { balance, rewardType, gasStore, claimType } = option as ClaimOption;
  const buttonText =
    claimType === ClaimType.Claim ? fm('claim') : fm('principalWithdrawal');

  const errorText = (() => {
    if (BigNumber(gasStore.amount || 0).gt(balance)) {
      return fm('token_balance_due', { token: gasStore.symbol });
    }
  })();

  const buttonDisabled = (() => {
    if (rewardType === RewardType.None || rewardType === RewardType.Queue) {
      return true;
    }

    if (BigNumber(gasStore.price || 0).gt(balance)) {
      return true;
    }

    if (!gasStore.hasGasFee) {
      return true;
    }
  })();

  return {
    errorText,
    buttonText,
    buttonDisabled,
  };
};
